import { logger } from '@libp2p/logger';
import { PutFailedError, NotFoundError, DeleteFailedError } from 'interface-store';
import drain from 'it-drain';
import { pushable } from 'it-pushable';
import { BaseDatastore } from './base.js';
const log = logger('datastore:core:tiered');
/**
 * A datastore that can combine multiple stores. Puts and deletes
 * will write through to all datastores. Has and get will
 * try each store sequentially. Query will always try the
 * last one first.
 *
 */
export class TieredDatastore extends BaseDatastore {
    stores;
    constructor(stores) {
        super();
        this.stores = stores.slice();
    }
    async put(key, value, options) {
        try {
            await Promise.all(this.stores.map(async (store) => { await store.put(key, value, options); }));
            return key;
        }
        catch (err) {
            throw new PutFailedError(err.message);
        }
    }
    async get(key, options) {
        for (const store of this.stores) {
            try {
                const res = await store.get(key, options);
                if (res != null)
                    return res;
            }
            catch (err) {
                log.error(err);
            }
        }
        throw new NotFoundError();
    }
    async has(key, options) {
        for (const s of this.stores) {
            if (await s.has(key, options)) {
                return true;
            }
        }
        return false;
    }
    async delete(key, options) {
        try {
            await Promise.all(this.stores.map(async (store) => { await store.delete(key, options); }));
        }
        catch (err) {
            throw new DeleteFailedError(err.message);
        }
    }
    async *putMany(source, options = {}) {
        let error;
        const pushables = this.stores.map(store => {
            const source = pushable({
                objectMode: true
            });
            drain(store.putMany(source, options))
                .catch(err => {
                // store threw while putting, make sure we bubble the error up
                error = err;
            });
            return source;
        });
        try {
            for await (const pair of source) {
                if (error != null) {
                    throw error;
                }
                pushables.forEach(p => p.push(pair));
                yield pair.key;
            }
        }
        finally {
            pushables.forEach(p => p.end());
        }
    }
    async *deleteMany(source, options = {}) {
        let error;
        const pushables = this.stores.map(store => {
            const source = pushable({
                objectMode: true
            });
            drain(store.deleteMany(source, options))
                .catch(err => {
                // store threw while deleting, make sure we bubble the error up
                error = err;
            });
            return source;
        });
        try {
            for await (const key of source) {
                if (error != null) {
                    throw error;
                }
                pushables.forEach(p => p.push(key));
                yield key;
            }
        }
        finally {
            pushables.forEach(p => p.end());
        }
    }
    batch() {
        const batches = this.stores.map(store => store.batch());
        return {
            put: (key, value) => {
                batches.forEach(b => { b.put(key, value); });
            },
            delete: (key) => {
                batches.forEach(b => { b.delete(key); });
            },
            commit: async (options) => {
                for (const batch of batches) {
                    await batch.commit(options);
                }
            }
        };
    }
    query(q, options) {
        return this.stores[this.stores.length - 1].query(q, options);
    }
    queryKeys(q, options) {
        return this.stores[this.stores.length - 1].queryKeys(q, options);
    }
}
//# sourceMappingURL=tiered.js.map